<template>
  <div class="chatter-composer" :class="{ 'chatter-composer--inline': inline }">
    <div class="chatter-composer__input-wrap">
      <textarea
        v-model="draft"
        class="chatter-composer__input"
        :rows="rows"
        :placeholder="placeholder"
        :disabled="disabled || submitting"
        :aria-label="ariaLabel"
        data-testid="chatter-composer-input"
        @keydown.enter.exact="onEnter"
      ></textarea>
      <v-icon v-if="!inline" class="chatter-composer__help">mdi-help-circle-outline</v-icon>
    </div>
    <button
      type="button"
      class="chatter-composer__post"
      :disabled="disabled || submitting || !draft.trim()"
      aria-label="Post comment"
      data-testid="chatter-composer-post"
      @click="post"
    >
      <v-icon>mdi-plus</v-icon>
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const MAX_ROWS = 5;

const props = defineProps({
  value: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Write a comment… type @ to mention someone",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /** A post is in flight: input and button are locked. */
  submitting: {
    type: Boolean,
    default: false,
  },
  /** Compact, full-width variant for replies under a comment. */
  inline: {
    type: Boolean,
    default: false,
  },
  /** When false the draft is kept after posting; the parent clears it by resetting `value`. */
  clearOnPost: {
    type: Boolean,
    default: true,
  },
  ariaLabel: {
    type: String,
    default: "Write a comment",
  },
});

const emit = defineEmits(["input", "update:value", "post"]);

const draft = ref(props.value);
const rows = computed(() => Math.min(MAX_ROWS, draft.value.split("\n").length));

watch(
  () => props.value,
  (next) => {
    if (next !== draft.value) draft.value = next;
  }
);

watch(draft, (next) => {
  emit("input", next);
  emit("update:value", next);
});

function post() {
  const text = draft.value.trim();
  if (!text || props.disabled || props.submitting) return;
  emit("post", text);
  if (props.clearOnPost) draft.value = "";
}

// Enter posts; Shift+Enter (not matched by `.exact`) keeps the browser's newline.
function onEnter(event) {
  if (event.isComposing) return;
  event.preventDefault();
  post();
}
</script>

<style scoped>
.chatter-composer {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  width: 600px;
  min-height: 76px;
  padding: 16px 24px;
  background: #ffffff;
  box-sizing: border-box;
}

.chatter-composer--inline {
  width: 100%;
  min-height: 0;
  padding: 0;
}

.chatter-composer__input-wrap {
  position: relative;
  flex: 1;
}

.chatter-composer__input {
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  padding-right: 36px;
  resize: none;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #101828;
  box-sizing: border-box;
}

.chatter-composer__input::placeholder {
  /* Design uses #98a2b3, but that is 2.58:1 on white; #667085 keeps 4.97:1 (WCAG AA). */
  color: #667085;
}

.chatter-composer__input:focus {
  outline: none;
  border-color: #7f56d9;
  box-shadow: 0 0 0 3px rgba(127, 86, 217, 0.12);
}

.chatter-composer--inline .chatter-composer__input {
  padding-right: 14px;
}

.chatter-composer__help {
  position: absolute;
  right: 12px;
  top: 12px;
  color: #667085;
  pointer-events: none;
}

.chatter-composer__post {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border: 1px solid #7f56d9;
  border-radius: 10px;
  background: #7f56d9;
  color: #ffffff;
  cursor: pointer;
}

.chatter-composer__post:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chatter-composer__post:focus-visible {
  outline: 2px solid #344054;
  outline-offset: 2px;
}
</style>
